import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { defaultLandingPath } from '@cvsi/shared';
import { TenantProvider, useTenant } from './lib/tenantContext';
import { ToastProvider } from './components/Toast';
import { AppShell } from './components/AppShell';
import { DeviceProvider } from './lib/device';

import { Loading } from './components/ui';

const LoginPage = lazy(() => import('./app/login/page'));
const DashboardPage = lazy(() => import('./app/dashboard/page'));
const WebhooksPage = lazy(() => import('./app/webhooks/page'));
const PoliciesPage = lazy(() => import('./app/policies/page'));
const ReportsPage = lazy(() => import('./app/reports/page'));
const SafetyIntelligencePage = lazy(() => import('./app/safety-intelligence/page'));
const ActivityPage = lazy(() => import('./app/activity/page'));
const EventsPage = lazy(() => import('./app/events/page'));
const EventDetailPage = lazy(() => import('./app/events/EventDetailPage'));
const CorridorsPage = lazy(() => import('./app/corridors/page'));
const UsagePage = lazy(() => import('./app/usage/page'));
const BillingPage = lazy(() => import('./app/billing/page'));
const SupportPage = lazy(() => import('./app/support/page'));
const SecurityPage = lazy(() => import('./app/security/page'));
const AuditCenterPage = lazy(() => import('./app/audit/page'));
const SettingsPage = lazy(() => import('./app/settings/page'));

/** Sends an unmatched path to the signed-in role's default workspace, not always '/dashboard' —
 * matters for roles like `billing` that can't see '/dashboard' at all. AppShell (mounted at that
 * destination) still redirects to /login when there's no session, so this stays safe when signed out. */
function DefaultRedirect() {
  const { context } = useTenant();
  return <Navigate to={context ? defaultLandingPath(context.role) : '/dashboard'} replace />;
}

export function App() {
  return (
    <DeviceProvider>
      <TenantProvider>
        <ToastProvider>
          <BrowserRouter>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<AppShell />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/api-keys" element={<Navigate to="/settings" replace />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/policies" element={<PoliciesPage />} />
                  <Route path="/branding" element={<Navigate to="/settings" replace />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/safety-intelligence" element={<SafetyIntelligencePage />} />
                  <Route path="/activity" element={<ActivityPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/events/:id" element={<EventDetailPage />} />
                  <Route path="/corridors" element={<CorridorsPage />} />
                  <Route path="/usage" element={<UsagePage />} />
                  <Route path="/billing" element={<BillingPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/docs" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/security" element={<SecurityPage />} />
                  <Route path="/audit" element={<AuditCenterPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
                <Route path="*" element={<DefaultRedirect />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </TenantProvider>
    </DeviceProvider>
  );
}
